/**
 * Design tokens — the single source of truth for color, type, space, shape,
 * elevation and motion (guide: mercury-look-and-feel-guia-de-adaptacao.md,
 * sections 4–6 and 9.5).
 *
 * `buildTokens()` derives every scale from the brand in OKLCH so a tenant
 * primary from `login-layout` can drive the action color without touching a
 * single component. Everything else (neutrals tempered by the brand hue,
 * semantics, type scale) is fixed: it is the Avaliahub identity, not the
 * tenant's. Components must reference tokens (`theme` or `var(--…)`), never
 * literal hexes.
 */
import {
  contrastRatio,
  fromOklch,
  rgbAlpha,
  scaleChroma,
  shiftLightness,
  toOklch,
} from './color';

export const brand = {
  /** Lima — the action color. */
  primary: '#A7E323',
  /** Verde-profundo — text primary and the hue that tempers the neutrals. */
  deepGreen: '#002518',
  /** Quase-preto — dark panel (login), favicon background, future dark theme. */
  ink: '#0D0D0D',
  white: '#FFFFFF',
} as const;

/**
 * M5 (plan): when true the tenant `primary_color` from `login-layout` drives
 * `--action`; when false every tenant gets the Avaliahub lima. Kept off while
 * the new look is being reviewed so the brand can be judged as designed.
 */
export const tenantActionColorEnabled = false;

export interface DesignTokens {
  color: {
    brand50: string;
    brand100: string;
    brand200: string;
    brand300: string;
    brand400: string;
    brand500: string;
    brand600: string;
    brand700: string;
    brand800: string;
    brand900: string;
    n50: string;
    n100: string;
    n150: string;
    n200: string;
    n300: string;
    n400: string;
    n600: string;
    n900: string;
    n950: string;
    canvas: string;
    surface: string;
    surfaceHover: string;
    borderHairline: string;
    borderStrong: string;
    textPrimary: string;
    textSecondary: string;
    textTertiary: string;
    action: string;
    actionHover: string;
    actionActive: string;
    actionFg: string;
    actionTint: string;
    focusRing: string;
    positive: string;
    positiveTint: string;
    warning: string;
    warningTint: string;
    negative: string;
    negativeTint: string;
    overlay: string;
  };
  font: {
    family: string;
    weightRegular: number;
    weightMedium: number;
  };
  type: Record<
    | 'caption'
    | 'label'
    | 'ui'
    | 'body'
    | 'titleSm'
    | 'title'
    | 'pageTitle'
    | 'moneyHero'
    | 'display',
    { size: number; lineHeight: string; weight: number; tracking: string }
  >;
  space: Record<1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 14 | 18, number>;
  radius: {
    control: number;
    card: number;
    modal: number;
    pill: number;
    check: number;
  };
  shadow: { float: string; modal: string };
  layout: {
    sidebarWidth: number;
    sidebarCollapsed: number;
    contentMax: number;
    contentGutter: number;
    drawerWidth: number;
  };
  motion: { easeOut: string; fast: string; base: string; drawer: string };
}

const tint = (hex: string): string =>
  fromOklch({ l: 0.965, c: 0.03, h: toOklch(hex).h });

/** Neutral tempered by the brand hue: chroma stays tiny so it still reads as grey. */
const neutral = (hue: number, l: number, c = 0.008): string =>
  fromOklch({ l, c, h: hue });

export function buildTokens(
  brandPrimary: string = brand.primary
): DesignTokens {
  const primary = toOklch(brandPrimary);
  const neutralHue = toOklch(brand.deepGreen).h;

  // Saturated brands lose ~15% chroma in UI (guide 9.2); the full color stays
  // in the logo. Dark brands lighten on hover instead of darkening.
  const action =
    primary.c > 0.15 ? scaleChroma(brandPrimary, 0.85) : brandPrimary;
  const isDarkBrand = primary.l < 0.6;
  const actionHover = shiftLightness(action, isDarkBrand ? 0.06 : -0.06);
  const actionActive = shiftLightness(action, isDarkBrand ? 0.1 : -0.1);

  const n900 = brand.deepGreen;
  const actionFg =
    contrastRatio(brand.white, action) >= 4.5 ? brand.white : n900;

  const scaleStep = (l: number, c: number): string =>
    fromOklch({ l, c: Math.min(c, primary.c), h: primary.h });

  const color: DesignTokens['color'] = {
    brand50: scaleStep(0.97, 0.03),
    brand100: scaleStep(0.93, 0.06),
    brand200: scaleStep(0.86, 0.1),
    brand300: scaleStep(0.76, 0.14),
    brand400: scaleStep(0.66, 0.17),
    brand500: action,
    brand600: actionHover,
    brand700: actionActive,
    brand800: scaleStep(0.35, 0.12),
    brand900: scaleStep(0.25, 0.08),

    n50: neutral(neutralHue, 0.975, 0.005),
    n100: neutral(neutralHue, 0.955, 0.006),
    n150: neutral(neutralHue, 0.935, 0.007),
    n200: neutral(neutralHue, 0.915, 0.008),
    n300: neutral(neutralHue, 0.84, 0.01),
    n400: neutral(neutralHue, 0.655, 0.012),
    n600: neutral(neutralHue, 0.49, 0.014),
    n900,
    n950: brand.ink,

    canvas: neutral(neutralHue, 0.975, 0.005),
    surface: brand.white,
    surfaceHover: neutral(neutralHue, 0.955, 0.006),
    borderHairline: neutral(neutralHue, 0.915, 0.008),
    borderStrong: neutral(neutralHue, 0.84, 0.01),
    textPrimary: n900,
    textSecondary: neutral(neutralHue, 0.49, 0.014),
    textTertiary: neutral(neutralHue, 0.655, 0.012),

    action,
    actionHover,
    actionActive,
    actionFg,
    actionTint: fromOklch({ l: 0.96, c: 0.04, h: primary.h }),
    focusRing: `0 0 0 2px ${brand.white}, 0 0 0 4px ${rgbAlpha(action, 0.45)}`,

    // Semantics are desaturated and pulled away from the lima so a green
    // state never reads as an action (guide 4.3 item 4).
    positive: '#1F7A55',
    positiveTint: tint('#1F7A55'),
    warning: '#B07A17',
    warningTint: tint('#B07A17'),
    negative: '#C24A4A',
    negativeTint: tint('#C24A4A'),
    overlay: rgbAlpha(n900, 0.32),
  };

  return {
    color,
    font: {
      family:
        '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      weightRegular: 400,
      weightMedium: 480,
    },
    type: {
      caption: {
        size: 12,
        lineHeight: '16px',
        weight: 400,
        tracking: '0.01em',
      },
      label: { size: 13, lineHeight: '18px', weight: 480, tracking: '0.005em' },
      ui: { size: 14, lineHeight: '20px', weight: 400, tracking: '0.005em' },
      body: { size: 16, lineHeight: '24px', weight: 400, tracking: '0' },
      titleSm: { size: 18, lineHeight: '24px', weight: 480, tracking: '0' },
      title: { size: 20, lineHeight: '28px', weight: 480, tracking: '0' },
      pageTitle: {
        size: 24,
        lineHeight: '32px',
        weight: 480,
        tracking: '0.01em',
      },
      moneyHero: {
        size: 36,
        lineHeight: '1',
        weight: 500,
        tracking: '-0.01em',
      },
      display: {
        size: 48,
        lineHeight: '1.1',
        weight: 480,
        tracking: '0.015em',
      },
    },
    space: {
      1: 4,
      2: 8,
      3: 12,
      4: 16,
      5: 20,
      6: 24,
      8: 32,
      10: 40,
      14: 56,
      18: 72,
    },
    radius: { control: 8, card: 12, modal: 16, pill: 999, check: 4 },
    shadow: {
      float: `0 8px 24px -8px ${rgbAlpha(
        n900,
        0.12
      )}, 0 2px 6px -2px ${rgbAlpha(n900, 0.06)}`,
      modal: `0 24px 64px -16px ${rgbAlpha(n900, 0.24)}`,
    },
    layout: {
      sidebarWidth: 240,
      sidebarCollapsed: 64,
      contentMax: 1280,
      contentGutter: 32,
      drawerWidth: 420,
    },
    motion: {
      easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
      fast: '120ms',
      base: '180ms',
      drawer: '240ms',
    },
  };
}

/** `transition` value for the given properties at the fast, ease-out pace. */
export const fastTransition = (
  motion: DesignTokens['motion'],
  ...properties: string[]
): string =>
  properties
    .map((prop) => `${prop} ${motion.fast} ${motion.easeOut}`)
    .join(', ');

/** CSS custom properties for `:root`, same names as the guide's kit (9.5). */
export function tokensToCssVars(tokens: DesignTokens): Record<string, string> {
  const { color, font, type, radius, shadow, layout, motion } = tokens;
  const vars: Record<string, string> = {
    '--brand-50': color.brand50,
    '--brand-500': color.brand500,
    '--brand-600': color.brand600,
    '--brand-700': color.brand700,
    '--n-50': color.n50,
    '--n-100': color.n100,
    '--n-150': color.n150,
    '--n-200': color.n200,
    '--n-300': color.n300,
    '--n-400': color.n400,
    '--n-600': color.n600,
    '--n-900': color.n900,
    '--n-950': color.n950,
    '--canvas': color.canvas,
    '--surface': color.surface,
    '--surface-hover': color.surfaceHover,
    '--border-hairline': color.borderHairline,
    '--border-strong': color.borderStrong,
    '--text-primary': color.textPrimary,
    '--text-secondary': color.textSecondary,
    '--text-tertiary': color.textTertiary,
    '--action': color.action,
    '--action-hover': color.actionHover,
    '--action-active': color.actionActive,
    '--action-fg': color.actionFg,
    '--action-tint': color.actionTint,
    '--focus-ring': color.focusRing,
    '--positive': color.positive,
    '--positive-tint': color.positiveTint,
    '--warning': color.warning,
    '--warning-tint': color.warningTint,
    '--negative': color.negative,
    '--negative-tint': color.negativeTint,
    '--overlay': color.overlay,
    '--font-ui': font.family,
    '--fw-regular': String(font.weightRegular),
    '--fw-medium': String(font.weightMedium),
    '--r-control': `${radius.control}px`,
    '--r-card': `${radius.card}px`,
    '--r-modal': `${radius.modal}px`,
    '--r-pill': `${radius.pill}px`,
    '--r-check': `${radius.check}px`,
    '--shadow-float': shadow.float,
    '--shadow-modal': shadow.modal,
    '--sidebar-w': `${layout.sidebarWidth}px`,
    '--sidebar-w-collapsed': `${layout.sidebarCollapsed}px`,
    '--content-max': `${layout.contentMax}px`,
    '--content-gutter': `${layout.contentGutter}px`,
    '--drawer-w': `${layout.drawerWidth}px`,
    '--ease-out': motion.easeOut,
    '--dur-fast': motion.fast,
    '--dur-base': motion.base,
    '--dur-drawer': motion.drawer,
  };
  (Object.keys(type) as (keyof DesignTokens['type'])[]).forEach((key) => {
    const cssKey = key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
    vars[`--text-${cssKey}`] = `${type[key].size}px`;
    vars[`--lh-${cssKey}`] = type[key].lineHeight;
  });
  return vars;
}

/** Shared default instance for code that runs outside the theme (rare). */
export const defaultTokens = buildTokens();
